import React, { useEffect, useMemo } from "react";
import { Card, CardContent } from "@shared/ui/card";
import { ArrowLeft, ArrowRight, Info } from "lucide-react";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { StepHelpers } from "@shared/components/Stepper";
import {
  useChoices,
  useGetAllSOPs,
  usePriorities,
} from "@features/mx/device-management/hooks";
import { toast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useNavigate } from "react-router-dom";

interface ServiceDetailsStepProps {
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  currentStepIndex?: number;
  totalSteps?: number;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  stepHelpers?: StepHelpers;
  remoteUtilityId: string;
}

export const ServiceDetailsStep: React.FC<ServiceDetailsStepProps> = ({
  data = {},
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep = true,
  isLastStep = false,
  currentStepIndex = 0,
  totalSteps = 3,
  setValidationError,
  clearValidationError,
  stepHelpers,
  remoteUtilityId,
}) => {
  const navigate = useNavigate();

  // Get saved data from stepHelpers on mount
  const savedStepData = useMemo(() => {
    if (stepHelpers) {
      const saved = stepHelpers.getStepData(currentStepIndex);
      return saved || {};
    }
    return {};
  }, [stepHelpers, currentStepIndex]);

  // ✅ FIX: Don't set default priority to "medium" - leave it empty
  const initialFormData = useMemo(() => {
    const merged = {
      operationType: savedStepData.operationType || data.operationType || "",
      sop: savedStepData.sop || data.sop || "",
      priority: savedStepData.priority || data.priority || "", // ✅ Changed from "medium" to ""
    };
    return merged;
  }, [savedStepData, data]);

  // Fetch service types from API
  const { data: serviceTypes } = useChoices({
    choice: "service_order_operation_type",
    remoteUtilityId,
  });

  // Fetch service types from API
  const { data: prioprityData } = usePriorities({
    choice: "priority",
  });
  console.log("Priority Data:", prioprityData);

  // Create form with merged initial data
  const form = FormService.createForm({
    fields: [],
    defaultValues: initialFormData,
    mode: "onChange",
    validateOnMount: false,
  });

  // Update form values when initialFormData changes
  useEffect(() => {
    Object.entries(initialFormData).forEach(([key, value]) => {
      // ✅ Only set value if it exists (not empty string)
      if (value) {
        form.setValue(key, value);
      }
    });
  }, [initialFormData, form]);

  // Watch operation type to fetch SOPs
  const selectedOperationType = form.watch("operationType");

  // Fetch SOPs based on selected operation type
  const { data: sops, isLoading: isLoadingSOPs } = useGetAllSOPs({
    remoteUtilityId,
    operationType: selectedOperationType
      ? Number(selectedOperationType)
      : undefined,
  });

  // Only reset SOP if operation type actually changes (not on mount)
  const previousOperationType = React.useRef(selectedOperationType);
  useEffect(() => {
    if (
      selectedOperationType &&
      previousOperationType.current &&
      selectedOperationType !== previousOperationType.current
    ) {
      form.setValue("sop", "");
    }
    previousOperationType.current = selectedOperationType;
  }, [selectedOperationType, form]);

  // Transform service types to options
  const operationTypeOptions = useMemo(() => {
    if (!serviceTypes) return [];
    return serviceTypes.map((type: any) => ({
      value: String(type.key),
      label: type.value,
    }));
  }, [serviceTypes]);

  // Transform pripority types to options
  const prioprityTypeOptions = useMemo(() => {
    if (!prioprityData) return [];
    return prioprityData.map((type: any) => ({
      value: String(type.key),
      label: type.value,
    }));
  }, [prioprityData]);

  // Transform SOPs to options
  const sopOptions = useMemo(() => {
    if (!sops?.result || !selectedOperationType) return [];
    return sops.result.map((sop: any) => ({
      value: String(sop.id),
      label: sop.name,
    }));
  }, [sops, selectedOperationType]);

  // ✅ Helper functions to get labels from values
  const getOperationTypeLabel = (value: string) => {
    const option = operationTypeOptions.find((opt) => opt.value === value);
    return option?.label || value;
  };

  const getSopLabel = (value: string) => {
    const option = sopOptions.find((opt) => opt.value === value);
    return option?.label || value;
  };

  const getPriorityLabel = (value: string) => {
    if (!value) return "";
    const option = prioprityTypeOptions.find(
      (opt) => opt.value === String(value)
    );
    return option?.label || value;
  };

  // Define form fields
  const serviceDetailsFields: FormField[] = useMemo(
    () => [
      {
        name: "operationType",
        type: "select",
        label: "Operation Type",
        required: true,
        placeholder: "Select operation type",
        defaultValue: initialFormData.operationType,
        options: operationTypeOptions,
        fullWidth: true,
      },
      {
        name: "sop",
        type: "select",
        label: "Standard Operating Procedure",
        required: true,
        placeholder: !selectedOperationType
          ? "Select operation type first"
          : isLoadingSOPs
          ? "Loading SOPs..."
          : sopOptions.length === 0
          ? "No SOPs available"
          : "Select SOP",
        defaultValue: initialFormData.sop,
        options: sopOptions,
        fullWidth: true,
        disabled:
          !selectedOperationType || isLoadingSOPs || sopOptions.length === 0,
      },
      {
        name: "priority",
        type: "select",
        label: "Priority",
        required: true,
        placeholder: "Select priority",
        defaultValue: initialFormData.priority,
        options: prioprityTypeOptions,
        fullWidth: true,
      },
    ],
    [
      operationTypeOptions,
      sopOptions,
      selectedOperationType,
      isLoadingSOPs,
      prioprityTypeOptions,
      initialFormData,
    ]
  );

  const handleNavigate = () => {
    navigate("/mx/device-management/service-order");
  };

  // Handle form submission
  const handleSubmit = async (formData: any) => {
    try {
      clearValidationError?.();

      // Validate the form
      const isValid = await FormService.validateForm(form);
      if (!isValid) {
        const errors = form.formState.errors;

        const firstError = Object.values(errors)[0];
        if (firstError && firstError.message) {
          setValidationError?.(String(firstError.message));
        }
        return;
      }

      // Get form values
      const formValues = form.getValues();

      // ✅ Create payload with both IDs and display names
      const payload = {
        // IDs for API calls
        operationType: formValues.operationType,
        sop: formValues.sop,
        priority: formValues.priority,

        // ✅ Display names for UI
        operationTypeName: getOperationTypeLabel(formValues.operationType),
        sopName: getSopLabel(formValues.sop),
        priorityName: getPriorityLabel(formValues.priority),
      };

      // Validate payload
      if (!payload.operationType || !payload.sop || !payload.priority) {
        setValidationError?.("Please fill in all required fields.");
        toast({
          title: "Error",
          description: "Please fill in all required fields.",
          variant: "destructive",
        });
        return;
      }

      // Save in stepHelpers
      if (stepHelpers) {
        stepHelpers.setStepData(currentStepIndex, payload);
      }

      // Notify parent
      onDataChange?.(payload);

      // Show success toast
      toast({
        title: "Success",
        description: "Service details saved successfully!",
      });

      // Move to next step
      onNext?.();
    } catch (error) {
      setValidationError?.("Failed to save form data. Please try again.");
      toast({
        title: "Error",
        description: "Failed to save form data. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle field changes
  const handleFieldChange = (fieldName: string, value: any) => {
    if (FormService.getFieldError(form, fieldName)) {
      form.clearErrors(fieldName);
    }

    clearValidationError?.();

    // const updatedData = { ...data, [fieldName]: value };
    // onDataChange?.(updatedData);
  };

  // Handle form reset
  const handleReset = () => {
    form.reset(initialFormData);
  };

  // Render info card about meter selection logic
  const renderMeterSelectionInfo = () => {
    const operationType = form.watch("operationType");
    if (!operationType) return null;

    return (
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
            <div>
              <h4 className="text-sm font-semibold text-blue-900 mb-2">
                How meter selection works
              </h4>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                <span className="text-sm font-semibold text-blue-900">
                  Meter Installation:
                </span>
                <p className="text-sm text-blue-800">
                  You'll see meters from both Inventory and Used Meters.
                </p>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                <span className="text-sm font-semibold text-blue-900">
                  Meter Disposal:
                </span>
                <p className="text-sm text-blue-800">
                  You'll see meters from Used Meters only.
                </p>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                <span className="text-sm font-semibold text-blue-900">
                  Meter Replacement:
                </span>
                <p className="text-sm text-blue-800">
                  You'll see all installed meters from the meter list.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Custom footer with navigation
  const renderCustomFooter = () => (
    <div className="flex justify-between pt-6 border-t border-gray-200 mt-6">
      <Button
        type="button"
        onClick={handleNavigate}
        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
      >
        <ArrowLeft size={16} />
        Back
      </Button>

      <Button
        type="submit"
        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
      >
        Next
        <ArrowRight size={16} />
      </Button>
    </div>
  );

  return (
    <div className="space-y-6">
      <DynamicForm
        fields={serviceDetailsFields}
        form={form}
        onSubmit={handleSubmit}
        onReset={handleReset}
        onFieldChange={handleFieldChange}
        loading={false}
        config={{
          showProgress: false,
          layout: "vertical",
          spacing: "none",
          submitButtonText: "Next",
          showResetButton: false,
          resetOnSubmit: false,
          classes: FIELD_CLASSES,
        }}
        renderCustomFooter={renderCustomFooter}
        className=""
        style={{
          background: "transparent",
          border: "none",
          boxShadow: "none",
        }}
      />
      {renderMeterSelectionInfo()}
    </div>
  );
};
